import { useEffect, useState, type RefObject } from 'react';
import { cx } from '../components/ui';
import { timecode } from '../lib/format';
import { chapterAt } from '../lib/timeline';
import type { Chapter } from '../queries/edit';

/**
 * The chapter list: where each named stretch of the recording begins, and
 * the one playing now. Click to go there. Named in the editor, on the
 * pieces of the finished video, so a chapter is a real place in what plays
 * here rather than a note somebody typed beside it.
 */
export function Chapters({
  chapters,
  mediaRef,
  frame = 'panel',
}: {
  chapters: Chapter[];
  mediaRef: RefObject<HTMLMediaElement | null>;
  /** In the side panel it heads a tab; as a card it sits under the video beside the links. */
  frame?: 'panel' | 'card';
}) {
  const [now, setNow] = useState(0);
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const onTime = () => setNow(media.currentTime);
    media.addEventListener('timeupdate', onTime);
    media.addEventListener('seeked', onTime);
    onTime();
    return () => {
      media.removeEventListener('timeupdate', onTime);
      media.removeEventListener('seeked', onTime);
    };
  }, [mediaRef]);
  const current = chapterAt(chapters, now);

  return (
    <section
      aria-labelledby="chapters-heading"
      className={cx(frame === 'card' && 'rounded-md border border-hairline bg-slate-900/40 px-4 py-3')}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id="chapters-heading"
          className={frame === 'card' ? 'text-caption font-medium uppercase tracking-wide text-chalk-faint' : 'text-h4 text-chalk'}
        >
          Chapters
        </h2>
        <span className="text-caption text-chalk-faint">
          {chapters.length} {chapters.length === 1 ? 'chapter' : 'chapters'}
        </span>
      </div>
      <ol className={cx('space-y-1', frame === 'card' ? 'mt-2 -mx-2' : 'mt-3')}>
        {chapters.map((chapter, index) => {
          const live = current === chapter;
          return (
            <li key={`${chapter.startSec}-${index}`}>
              <button
                type="button"
                onClick={() => {
                  const media = mediaRef.current;
                  if (!media) return;
                  media.currentTime = chapter.startSec;
                  void media.play().catch(() => {});
                }}
                className={cx(
                  'flex w-full items-start gap-3 rounded-md px-2 py-2 text-left transition-colors duration-120',
                  live ? 'bg-slate-800 text-chalk' : 'text-chalk-dim hover:bg-slate-900 hover:text-chalk',
                )}
              >
                <span className="w-11 shrink-0 pt-px font-mono text-caption tnum text-signal">{timecode(chapter.startSec)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body">{chapter.title}</span>
                  <span className="block text-caption text-chalk-faint">{timecode(chapter.endSec - chapter.startSec)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
